import { parseDate } from "@progress/kendo-intl";
import { Field, FormElement, FormRenderProps } from "@progress/kendo-react-form";
import { useEffect, useMemo, useState } from "react";
import allocationApi from "../../../api/allocation";
import contractsApi from "../../../api/contracts";
import { getComponents } from "../../../api/vanilla";
import { IApiResults, useFetch } from "../../../hooks/useApi";
import { useVanillaUtil } from "../../../hooks/useVanilla";
import { BusinessType } from "../../../types/businessType";
import DateUtility from "../../../utilities/dateUtilities";
import { StrikeType } from "../../../utilities/vanilla/vanillaEnums";
import { requiredCheckboxValidator } from "../../../validators";
import { LoadingIndicator } from "../../LoadingIndicator";
import FormCheckbox from "../../form/FormCheckBox";
import VanillaQuote from "./VanillaQuote";
import VanillaStructure from "./VanillaStructure";
import VanillaSubmit from "./VanillaSubmit";

export interface GetIntraDayPricesRequest {
  instrumentType: number;
  tickerCode: string;
  startDate: string;
  endDate: string;
}

type Props = {
  formRenderProps: FormRenderProps;
  step: number;
  product: any;
  marketStatsResults: IApiResults;
  structureName: string;
  showAnalytics: boolean;
  isDealEntry?: boolean;
  onLoaded?: () => void;
};

type UserDataItem = {
  date: string;
  volume: number;
  prices: UserStrike[];
};

type UserStrike = {
  componentId: number;
  strike: number;
  adder: number;
  avgAdder: number;
};

// This component fetches intraday prices before rendering the structuring tool
const VanillaStructureContainer = ({
  formRenderProps,
  step,
  product,
  marketStatsResults,
  structureName,
  showAnalytics,
  isDealEntry,
  onLoaded,
}: Props) => {
  const { commodity, company, instrument } = product;
  const { calendarMonthOffset } = instrument;

  const contractStart = formRenderProps.valueGetter("contract.start");
  const contractEnd = formRenderProps.valueGetter("contract.end");
  const previousQuote = formRenderProps.valueGetter("prices");
  const [startMonth, endMonth] = [contractStart, contractEnd].map((string) => {
    let date = DateUtility.parseMonth(string);
    return date;
  });
  const startDate = DateUtility.formatDateYMD(startMonth);
  const endDate = DateUtility.formatDateYMD(endMonth);

  const params: GetIntraDayPricesRequest = {
    tickerCode: commodity.abbreviation,
    instrumentType: instrument.id,
    startDate,
    endDate,
  };

  const { data: pricesData } = useFetch(contractsApi.getIntraDayPrices, params);

  const { data } = useFetch(contractsApi.getBusinessDays, params);
  const businessDays = data ? (Object.values(data.businessDays) as number[]) : null;

  const structureId = formRenderProps.valueGetter("structureId");
  const { data: componentsData } = useFetch(getComponents, structureId);

  const components = componentsData?.components || [];

  const prices = pricesData?.prices ? Object.values(pricesData.prices) : [];

  const getAllocationsApi = useFetch(allocationApi.getAllocations, company.id);

  const allocations = useMemo(() => {
    const source = getAllocationsApi.data?.allocations || [];
    return source.filter((allocation: any) => {
      const isDerivative = allocation.businessTypeIds.some(
        (id: number) => id === BusinessType.Derivatives,
      );
      const matchesCommodityType =
        allocation.commodityTypes.length === 0 ||
        allocation.commodityTypes.some((commodityType: string) => commodityType === commodity.type);

      return isDerivative && matchesCommodityType;
    });
  }, [getAllocationsApi.data?.allocations, commodity.type]);

  const transactionDirection = formRenderProps.valueGetter("direction");
  const vanillaUtil = useVanillaUtil(
    components,
    startMonth,
    endMonth,
    prices as number[],
    transactionDirection,
    businessDays,
  );

  const [isFormPrefilled, setIsFormPrefilled] = useState(false);

  // When editing a quote, check if the user modified the start or end date or the structure
  // If not, prefill the structuring step with the values from when previously quoted
  // If yes, reset everything to defaults.
  useEffect(() => {
    if (vanillaUtil && previousQuote && previousQuote.length && !isFormPrefilled) {
      let prevStartDate = parseDate(previousQuote[0].date);
      let prevEndDate = parseDate(previousQuote[previousQuote.length - 1].date);

      // dates in db are always contract dates. convert to calendar dates here if there is an offset
      prevStartDate = DateUtility.addMonthOffset(prevStartDate, -1 * calendarMonthOffset);
      prevEndDate = DateUtility.addMonthOffset(prevEndDate, -1 * calendarMonthOffset);

      const isDateMatch =
        prevStartDate.getTime() === startMonth.getTime() &&
        prevEndDate.getTime() === endMonth.getTime();

      const isStructureMatch =
        previousQuote[0].prices.every((item: UserStrike, i: number) => {
          return item.componentId === components[i]?.componentId;
        }) && previousQuote[0].prices.length === components.length;

      const isMatch = isDateMatch && isStructureMatch;

      // The user stepped through step 1 without changing the start or end date or the structure
      if (isMatch) {
        const commonStrike = formRenderProps.valueGetter("commonStrike");
        const fixedStrike = formRenderProps.valueGetter("strikeType") === "Fixed";
        if (!commonStrike) {
          vanillaUtil.setIsCommonStrike(false);
        }
        previousQuote.forEach(({ volume, prices }: UserDataItem, i: number) => {
          vanillaUtil.setVolume(volume, i);

          if (!commonStrike) {
            prices.forEach(({ adder, strike }: UserStrike, j: number) => {
              let strikeType = StrikeType.Fixed;
              if (fixedStrike) {
                vanillaUtil.setStrike(strike, j, strikeType, i);
              } else {
                strikeType = StrikeType.Adder;
                vanillaUtil.setStrike(adder, j, strikeType, i);
              }
            });
          }
        });

        // If common strike was selected, set the avg adder or fixed strike
        if (commonStrike) {
          const { prices }: UserDataItem = previousQuote[0];
          let STRIKE_TYPE = StrikeType.Fixed;
          if (fixedStrike) {
            prices.forEach(({ strike }: UserStrike, j: number) => {
              vanillaUtil.setStrike(strike, j, STRIKE_TYPE);
            });
          } else {
            STRIKE_TYPE = StrikeType.Adder;
            prices.forEach(({ avgAdder }: UserStrike, j: number) => {
              vanillaUtil.setStrike(avgAdder, j, STRIKE_TYPE);
            });
          }
        }
      }
      setIsFormPrefilled(true);
    }
  }, [vanillaUtil]);

  const isLoading = vanillaUtil === null;
  useEffect(() => {
    formRenderProps.onChange("loaded", { value: !isLoading });
  }, [isLoading]);

  const direction = formRenderProps.valueGetter("direction");
  useEffect(() => {
    if (direction && !isLoading) {
      vanillaUtil.setDirection(direction);
    }
  }, [direction, isLoading]);

  return (
    <>
      <LoadingIndicator loading={isLoading} />
      {!isLoading && (step === 1 || (isDealEntry && step >= 1)) && (
        <VanillaStructure
          formRenderProps={formRenderProps}
          calculator={vanillaUtil}
          allocations={allocations}
          commodity={commodity}
          product={product}
          readOnly={isDealEntry && step != 1}
          onLoaded={onLoaded}
        />
      )}

      {isDealEntry && <span style={{ margin: "1rem" }} />}

      {!isLoading && step === 2 && (
        <>
          <VanillaQuote
            formRenderProps={formRenderProps}
            calculator={vanillaUtil}
            product={product}
            marketStatsResults={marketStatsResults}
            structureName={structureName}
            showAnalytics={showAnalytics}
            isDealEntry={isDealEntry}
            onLoaded={onLoaded}
          />
        </>
      )}
      {!isLoading && step === 3 && (
        <VanillaSubmit
          formRenderProps={formRenderProps}
          calculator={vanillaUtil}
          product={product}
          isDealEntry={isDealEntry}
        />
      )}
      <FormElement style={{ visibility: "hidden", display: "none" }}>
        <Field
          name="loaded"
          component={FormCheckbox}
          validator={requiredCheckboxValidator}
          showValidationMessage={false}
        />
      </FormElement>
    </>
  );
};

export default VanillaStructureContainer;
